import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { useMode } from '../../context/ModeContext';

const OrganizationProtectedRoute = ({ 
  children, 
  requiredOrgRoles = [], 
  requiredPlatformRoles = [],
  requireActiveSubscription = false,
  fallbackRoute = '/dashboard',
  requireOrganizationAccess = true
}) => {
  const { currentUser, isAuthenticated, hasOrgRole, hasPlatformRole, loading: authLoading } = useAuth();
  const { currentOrganization, availableOrganizations, loading: orgLoading } = useOrganization();
  const { hasOrganizationAccess } = useMode();
  const location = useLocation();

  // Show loading state while auth or organization data is resolving
  if (authLoading || orgLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated || !currentUser) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Determine access using organization membership data to avoid false negatives
  const hasMembership = useMemo(() => {
    if (!currentUser) return false;

    const membershipIds = new Set();

    if (currentUser.organizationId) {
      membershipIds.add(String(currentUser.organizationId));
    }

    if (currentOrganization?._id) {
      membershipIds.add(String(currentOrganization._id));
    }

    if (Array.isArray(availableOrganizations) && availableOrganizations.length > 0) {
      availableOrganizations.forEach(org => {
        if (org?._id) {
          membershipIds.add(String(org._id));
        }
      });
    }

    return membershipIds.size > 0;
  }, [currentUser, currentOrganization, availableOrganizations]);

  const blockedByPlatformRole = currentUser?.platformRole && ['super_admin', 'platform_admin'].includes(currentUser.platformRole);
  const computedOrgAccess = hasMembership && !blockedByPlatformRole;
  const effectiveOrgAccess = computedOrgAccess || (typeof hasOrganizationAccess === 'function' ? hasOrganizationAccess() : false);

  // Check if route requires organization access but user doesn't have it
  if (requireOrganizationAccess && !effectiveOrgAccess) {
    console.log('OrganizationProtectedRoute: Access denied - user does not have organization access');
    return <Navigate to={fallbackRoute} replace />;
  }

  // Check if user has required organization role
  if (requiredOrgRoles.length > 0 && !hasOrgRole(requiredOrgRoles)) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.982 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-yellow-900 mb-2">Access Restricted</h2>
          <p className="text-yellow-700 mb-4">
            You don't have the required organization role to access this page.
          </p>
          <p className="text-sm text-yellow-600">
            Required roles: {requiredOrgRoles.join(', ')}
          </p>
          <div className="mt-6">
            <Navigate to={fallbackRoute} replace />
          </div>
        </div>
      </div>
    );
  }

  // Check if user has required platform role
  if (requiredPlatformRoles.length > 0 && !hasPlatformRole(requiredPlatformRoles)) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700 mb-4">
            You don't have the required platform permissions to access this page.
          </p>
          <p className="text-sm text-red-600">
            Required platform roles: {requiredPlatformRoles.join(', ')}
          </p>
          <div className="mt-6">
            <Navigate to={fallbackRoute} replace />
          </div>
        </div>
      </div>
    );
  }

  // Check organization subscription if required
  if (requireActiveSubscription && currentOrganization) {
    const isSubscriptionActive = currentOrganization.subscriptionTier !== 'free' || 
                                 (currentOrganization.trialEndsAt && new Date(currentOrganization.trialEndsAt) > new Date());
    
    if (!isSubscriptionActive) {
      return (
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-blue-900 mb-2">Upgrade Required</h2>
            <p className="text-blue-700 mb-4">
              This feature requires an active subscription to access.
            </p>
            <div className="space-x-4">
              <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
                Upgrade Now
              </button>
              <Navigate to={fallbackRoute} replace />
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default OrganizationProtectedRoute;